import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';

const STATUS_MAP = {
  submitted:   { label:'Submitted',   bg:'var(--green-bg)',   color:'var(--green)'  },
  pending_po:  { label:'Pending PO',  bg:'var(--amber-bg)',   color:'var(--amber)'  },
  po_received: { label:'PO Received', bg:'var(--blue-light)', color:'var(--blue)'   },
  sent_to_hr:  { label:'Sent to HR',  bg:'var(--purple-bg)',  color:'var(--purple)' },
  invoiced:    { label:'Invoiced',    bg:'var(--green-bg)',   color:'var(--green)'  },
};

const PAGE_SIZE = 10;

export default function JobList() {
  const { user, can } = useAuth();
  const navigate = useNavigate();


  const [jobs,        setJobs]        = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [locations,   setLocations]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filters,     setFilters]     = useState({ dealership:'', location:'', status:'', date:'', dateFrom:'', dateTo:'' });
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [exporting,   setExporting]   = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceInput,   setInvoiceInput]   = useState('');
  const [editingPO,      setEditingPO]      = useState(null);
  const [poInput,        setPoInput]        = useState('');
  const [deletingId,     setDeletingId]     = useState(null);

  const [bulkMode,       setBulkMode]       = useState(false);
  const [addlMode,       setAddlMode]       = useState(false);
  const [addlSelected,   setAddlSelected]   = useState(new Set());
  const [addlPO,         setAddlPO]         = useState('');
  const [addlTotal,      setAddlTotal]      = useState('');
  const [addlSplitAmts,  setAddlSplitAmts]  = useState({});
  const [showAddlSplit,  setShowAddlSplit]  = useState(false);
  const [addlSaving,     setAddlSaving]     = useState(false);
  const [bulkSelected,   setBulkSelected]   = useState(new Set());
  const [bulkPO,         setBulkPO]         = useState('');
  const [bulkTotal,      setBulkTotal]      = useState('');
  const [splitAmounts,   setSplitAmounts]   = useState({});
  const [showSplit,      setShowSplit]      = useState(false);
  const [bulkSaving,     setBulkSaving]     = useState(false);
  const searchTimer = useRef(null);

  // Use AWST (UTC+8) for today's date to match server timezone
  const today = new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0];

  // Fetch jobs — when searching, fetch all records; otherwise fetch by date
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // Staff can only see today's data
      const canEdit = can('canEditJobs') || can('canDownloadExcel');
      if (search.trim() && canEdit) {
        // Only owners/managers can search all records
        params.limit = 500;
      } else {
        // Staff always see today only
        if (addlMode && filters.dateFrom && filters.dateTo) {
          params.from = filters.dateFrom;
          params.to   = filters.dateTo;
        } else {
          params.date = canEdit ? (filters.date || today) : today;
        }
      }
      if (filters.dealership) params.dealership = filters.dealership;
      if (filters.location)   params.location   = filters.location;
      if (filters.status)     params.status     = filters.status;
      const { data } = await api.get('/jobs', { params });
      setJobs(data.jobs);
      setPage(1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters, search, addlMode]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => { fetchJobs(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchJobs]);
  useEffect(() => {
    api.get('/jobs/dealerships').then(({data})=>setDealerships(data.dealerships)).catch(()=>{});
    api.get('/jobs/locations').then(({data})=>setLocations(data.locations)).catch(()=>{});
  }, []);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filters, search]);

  const exportExcel = async (dealershipName) => {
    setExporting(true);
    try {
      const params = { date: filters.date || today };
      if (dealershipName) params.dealership = dealershipName;
      const res = await api.get('/jobs/export', { params, responseType:'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `${dealershipName||'AllDealerships'}-export.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('No jobs found for this date.'); }
    finally { setExporting(false); setExportMenuOpen(false); }
  };

  const savePO = async (jobId, entryIdx) => {
    if (!poInput.trim()) return;
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      const job = data.job;
      const entries = job.poEntries && job.poEntries.length > 0
        ? [...job.poEntries]
        : [{ poNumber:job.poNumber||'', price:job.price||0, services:job.coreServices||[] }];
      if (entries[entryIdx]) entries[entryIdx] = { ...entries[entryIdx], poNumber:poInput.toUpperCase() };
      await api.patch(`/jobs/${jobId}`, { poEntries:entries, poNumber:entries[0]?.poNumber||'', hasPO:entries.some(e=>e.poNumber), status:'po_received' });
      setEditingPO(null); setPoInput(''); fetchJobs();
    } catch { alert('Failed to save PO'); }
  };

  const saveInvoice = async (jobId, entryIdx) => {
    const idx = entryIdx !== undefined ? entryIdx : 0;
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      const job = data.job;
      // Build entries array from existing poEntries
      const entries = (job.poEntries && job.poEntries.length > 0)
        ? job.poEntries.map(e => ({ ...e }))
        : [{ poNumber:job.poNumber||'', price:job.price||0, services:job.coreServices||[], invoiceNumber:job.invoiceNumber||'' }];
      // Update invoice on the specific entry
      if (entries[idx]) {
        entries[idx] = { ...entries[idx], invoiceNumber: invoiceInput.toUpperCase() };
      }
      // Job-level invoice = first non-empty invoice across all entries
      const jobInvoice = entries.find(e=>e.invoiceNumber)?.invoiceNumber || '';
      const hasPO = entries.some(e=>e.poNumber?.trim());
      await api.patch(`/jobs/${jobId}`, {
        poEntries:     entries,
        invoiceNumber: jobInvoice,
        status:        jobInvoice ? 'invoiced' : (hasPO ? 'po_received' : 'pending_po'),
      });
      setEditingInvoice(null); setInvoiceInput(''); fetchJobs();
    } catch (err) {
      console.error('Invoice save error:', err);
      alert('Failed to save invoice: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (jobId, label) => {
    if (!await confirmDelete(`Delete job "${label||jobId}"? All PO entries, invoice numbers and edit history will be removed.`)) return;
    setDeletingId(jobId);
    try { await api.delete(`/jobs/${jobId}`); fetchJobs(); }
    catch (err) { alert(err.response?.data?.message||'Failed to delete'); }
    finally { setDeletingId(null); }
  };

  // Filter by search term
  const q = search.trim().toLowerCase();
  const filteredJobs = q
    ? jobs.filter(j =>
        (j.stockNum||'').toLowerCase().includes(q) ||
        (j.poNumber||'').toLowerCase().includes(q) ||
        (j.invoiceNumber||'').toLowerCase().includes(q) ||
        (j.dealership||'').toLowerCase().includes(q) ||
        (j.vinNum||'').toLowerCase().includes(q) ||
        (j.rego||'').toLowerCase().includes(q)
      )
    : jobs;

  // Expand multi-PO rows
  const allRows = filteredJobs.flatMap(job => {
    const rows = job.poEntries && job.poEntries.length > 0
      ? job.poEntries.map((entry,eIdx) => ({
          ...entry,
          services:(entry.services&&entry.services.length>0)?entry.services:(eIdx===0?(job.coreServices||[]):[]),
          isFirst:eIdx===0, eIdx, job,
        }))
      : [{ poNumber:job.poNumber||'', price:job.price||0, invoiceNumber:job.invoiceNumber||'', services:job.coreServices||[], isFirst:true, eIdx:0, job }];
    return rows;
  });

  // Pagination
  const totalPages = Math.ceil(allRows.length / PAGE_SIZE);
  const pagedRows  = allRows.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  const totalRevenue = filteredJobs.reduce((a,j)=>a+(j.price||0),0);
  const pendingCount = filteredJobs.filter(j=>!j.poNumber).length;
  const dealershipsInView = [...new Set(jobs.map(j=>j.dealership))];

  const ExcelButtons = () => (
    <div style={{ display:'flex', gap:'8px' }}>
      {can('canUploadExcel') && (
        <button onClick={()=>navigate('/jobs/upload')}
          style={{ ...secBtn }}>
          <i className="ti ti-upload" aria-hidden="true"/> Upload
        </button>
      )}
      {can('canDownloadExcel') && (
        <div style={{ position:'relative' }}>
          <button onClick={()=>setExportMenuOpen(o=>!o)} disabled={exporting} style={secBtn}>
            <i className="ti ti-download" aria-hidden="true"/> {exporting?'...':'Download'}
          </button>
          {exportMenuOpen && (
            <>
              <div onClick={()=>setExportMenuOpen(false)} style={{ position:'fixed',inset:0,zIndex:199 }}/>
              <div style={{ position:'absolute', top:'calc(100% + 4px)', right:0, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', boxShadow:'var(--shadow)', zIndex:200, minWidth:'210px', overflow:'hidden' }}>
                <button onClick={()=>exportExcel()} style={menuItem}>All Dealerships (combined)</button>
                {dealershipsInView.length > 0 && <div style={{ height:'1px', background:'var(--border)' }}/>}
                {dealershipsInView.map(d=>(
                  <button key={d} onClick={()=>exportExcel(d)} style={menuItem}>{d} only</button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Additional PO helpers
  const toggleAddlMode = () => {
    const thirtyDaysAgo = new Date(new Date().getTime() - 30*24*60*60*1000 + 8*60*60*1000).toISOString().split('T')[0];
    setAddlMode(b => {
      if (!b) setFilters(f=>({...f, dateFrom: thirtyDaysAgo, dateTo: today, date: ''}));
      else setFilters(f=>({...f, dateFrom:'', dateTo:'', date:''}));
      return !b;
    });
    setAddlSelected(new Set()); setAddlPO(''); setAddlTotal(''); setAddlSplitAmts({}); setShowAddlSplit(false);
  };
  const cancelAddl = () => { setAddlMode(false); setAddlSelected(new Set()); setAddlPO(''); setAddlTotal(''); setAddlSplitAmts({}); setShowAddlSplit(false); };
  const toggleAddlRow = (jobId) => {
    setAddlSelected(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };
  const autoSplitAddl = (total, selected) => {
    if (!selected.size || !total) return {};
    const ids = [...selected];
    const each = +(parseFloat(total) / ids.length).toFixed(2);
    const amounts = {};
    ids.forEach((id, i) => { amounts[id] = i === ids.length-1 ? +(parseFloat(total) - each*(ids.length-1)).toFixed(2) : each; });
    return amounts;
  };
  const handleAddlTotalChange = (val) => { setAddlTotal(val); setAddlSplitAmts(autoSplitAddl(val, addlSelected)); };
  const handleShowAddlSplit = () => {
    if (!addlPO.trim()) { alert('Enter a PO number'); return; }
    if (!addlTotal || parseFloat(addlTotal) <= 0) { alert('Enter a total amount'); return; }
    if (addlSelected.size === 0) { alert('Select at least one job'); return; }
    setAddlSplitAmts(autoSplitAddl(addlTotal, addlSelected));
    setShowAddlSplit(true);
  };
  const handleConfirmAddl = async () => {
    setAddlSaving(true);
    try {
      await api.patch('/jobs/bulk-po', {
        jobIds: [...addlSelected],
        poNumber: addlPO.toUpperCase().trim(),
        amounts: addlSplitAmts,
        isAdditional: true,
      });
      cancelAddl();
      fetchJobs();
    } catch (err) { alert(err.response?.data?.message || 'Failed to assign additional PO'); }
    finally { setAddlSaving(false); }
  };
  const addlAllocated = Object.values(addlSplitAmts).reduce((a,b)=>a+b,0);
  const addlRemaining = parseFloat(addlTotal||0) - addlAllocated;

  // Bulk PO helpers
  const toggleBulkMode = () => { setBulkMode(b=>!b); setBulkSelected(new Set()); setBulkPO(''); setBulkTotal(''); setSplitAmounts({}); setShowSplit(false); };
  const cancelBulk = () => { setBulkMode(false); setBulkSelected(new Set()); setBulkPO(''); setBulkTotal(''); setSplitAmounts({}); setShowSplit(false); };
  const toggleBulkRow = (jobId) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };
  const autoSplit = (total, selected) => {
    if (!selected.size || !total) return {};
    const ids = [...selected];
    const each = +(parseFloat(total) / ids.length).toFixed(2);
    const amounts = {};
    ids.forEach((id, i) => {
      amounts[id] = i === ids.length-1 ? +(parseFloat(total) - each*(ids.length-1)).toFixed(2) : each;
    });
    return amounts;
  };
  const handleBulkTotalChange = (val) => {
    setBulkTotal(val);
    setSplitAmounts(autoSplit(val, bulkSelected));
  };
  const handleShowSplit = () => {
    if (!bulkPO.trim()) { alert('Enter a PO number'); return; }
    if (!bulkTotal || parseFloat(bulkTotal) <= 0) { alert('Enter a total amount'); return; }
    if (bulkSelected.size === 0) { alert('Select at least one job'); return; }
    setSplitAmounts(autoSplit(bulkTotal, bulkSelected));
    setShowSplit(true);
  };
  const handleConfirmBulk = async () => {
    setBulkSaving(true);
    try {
      await api.patch('/jobs/bulk-po', {
        jobIds: [...bulkSelected],
        poNumber: bulkPO.toUpperCase().trim(),
        amounts: splitAmounts,
      });
      cancelBulk();
      fetchJobs();
    } catch (err) { alert(err.response?.data?.message || 'Failed to assign PO'); }
    finally { setBulkSaving(false); }
  };
  const bulkAllocated = Object.values(splitAmounts).reduce((a,b)=>a+b,0);
  const bulkRemaining = parseFloat(bulkTotal||0) - bulkAllocated;

  // Get the extra notes specific to a PO row (not the combined job-level string)
  const getRowExtraNotes = (job, eIdx) => {
    if (job.poEntries && job.poEntries[eIdx]?.extraNotes) return job.poEntries[eIdx].extraNotes;
    if (eIdx === 0) return job.extraNotes || ''; // fallback for legacy jobs without per-PO notes
    return '';
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', fontFamily:'Inter,sans-serif' }}>

      {/* ── Top bar ── */}
      <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)' }}>
        {/* Row 1: Title + buttons */}
        <div style={{ padding:'0 16px', height:'52px', display:'flex', alignItems:'center', gap:'10px' }}>
          <span style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', flexShrink:0 }}>Daily Sheet</span>
          <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center' }}>
            <ExcelButtons/>
            {(can('canEditJobs') && can('canDownloadExcel')) && (
              <>
{/* Bulk PO button hidden - functionality intact */}
                <button onClick={toggleAddlMode}
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:addlMode?'var(--blue)':'transparent', border:`1px solid ${addlMode?'var(--blue)':'var(--border-md)'}`, borderRadius:'var(--radius)', padding:'8px 14px', color:addlMode?'#fff':'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                  <i className="ti ti-plus" aria-hidden="true"/> Additional PO
                </button>
              </>
            )}
            <button onClick={()=>navigate('/jobs/new-entry')}
              style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'8px 16px', color:'#fff', fontSize:'13px', fontWeight:'500', cursor:'pointer', fontFamily:'inherit' }}>
              <i className="ti ti-plus" aria-hidden="true"/> New Entry
            </button>
          </div>
        </div>
        {/* Row 2: Search bar — only for roles with canSearchJobs */}
        {can('canSearchJobs') && (
          <div style={{ padding:'0 16px 10px', position:'relative' }}>
            <i className="ti ti-search" style={{ position:'absolute', left:'26px', top:'50%', transform:'translateY(-60%)', color:'var(--hint)', fontSize:'15px', pointerEvents:'none' }} aria-hidden="true"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search all records — stock, PO, VIN, dealership..."
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'9px 12px 9px 36px', fontSize:'14px', color:'var(--text)', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor='var(--blue)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
            {search && (
              <button onClick={()=>setSearch('')} style={{ position:'absolute', right:'24px', top:'50%', transform:'translateY(-60%)', background:'none', border:'none', cursor:'pointer', color:'var(--hint)', display:'flex', alignItems:'center' }}>
                <i className="ti ti-x" style={{ fontSize:'14px' }} aria-label="Clear"/>
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ maxWidth:'1400px', margin:'0 auto', padding:'14px 16px 100px' }}>

        {/* Pending PO warning */}
        {pendingCount > 0 && (
          <div style={{ background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'var(--amber)' }}>
            <i className="ti ti-alert-triangle" aria-hidden="true"/>
            <span><strong>{pendingCount}</strong> job{pendingCount>1?'s':''} missing PO number</span>
          </div>
        )}

        {/* Bulk PO bar */}
        {bulkMode && !showSplit && (
          <div style={{ background:'var(--blue-light)', border:'1px solid var(--blue-mid)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <span style={{ fontSize:'13px', color:'var(--blue)', fontWeight:'500' }}>{bulkSelected.size} job{bulkSelected.size!==1?'s':''} selected</span>
            <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
              <input value={bulkPO} onChange={e=>setBulkPO(e.target.value.toUpperCase())}
                placeholder="PO number..." style={{ ...inlineInput, width:'140px', fontSize:'13px', padding:'7px 10px' }}/>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:'13px' }}>$</span>
                <input type="number" value={bulkTotal} onChange={e=>handleBulkTotalChange(e.target.value)}
                  placeholder="Total amount" style={{ ...inlineInput, width:'150px', fontSize:'13px', padding:'7px 10px 7px 24px' }}/>
              </div>
              <button onClick={handleShowSplit} style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'7px 14px', color:'#fff', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                <i className="ti ti-calculator" aria-hidden="true"/> Review Split
              </button>
              <button onClick={cancelBulk} style={{ background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'7px 12px', color:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Split review panel */}
        {showSplit && (
          <div style={{ background:'var(--surface)', border:'1px solid var(--blue)', borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px', flexWrap:'wrap', gap:'8px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)' }}>Review price split</span>
                <span style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'12px', fontFamily:'JetBrains Mono,monospace', fontWeight:'600', padding:'2px 8px', borderRadius:'20px' }}>{bulkPO}</span>
              </div>
              <div style={{ display:'flex', gap:'12px', alignItems:'center', fontSize:'13px', flexWrap:'wrap' }}>
                <span style={{ color:'var(--muted)' }}>Total: <strong style={{ color:'var(--text)', fontFamily:'JetBrains Mono,monospace' }}>${parseFloat(bulkTotal).toFixed(2)}</strong></span>
                <span style={{ color:'var(--muted)' }}>Allocated: <strong style={{ color:'var(--green)', fontFamily:'JetBrains Mono,monospace' }}>${bulkAllocated.toFixed(2)}</strong></span>
                <span style={{ fontSize:'12px', color: Math.abs(bulkRemaining)<0.01 ? 'var(--green)' : bulkRemaining>0 ? 'var(--amber)' : 'var(--red)' }}>
                  {Math.abs(bulkRemaining)<0.01 ? '✓ Balanced' : bulkRemaining>0 ? `$${bulkRemaining.toFixed(2)} unallocated` : `$${Math.abs(bulkRemaining).toFixed(2)} over`}
                </span>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'14px' }}>
              {[...bulkSelected].map(jobId => {
                const job = filteredJobs.find(j=>j._id===jobId);
                if (!job) return null;
                return (
                  <div key={jobId} style={{ display:'flex', alignItems:'center', gap:'10px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 14px', flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'JetBrains Mono,monospace', fontWeight:'500', color:'var(--blue)', minWidth:'80px' }}>{job.stockNum}</span>
                    <span style={{ color:'var(--muted)', fontSize:'13px', flex:1 }}>{(job.coreServices||[]).join(', ')||'—'}</span>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <span style={{ fontSize:'13px', color:'var(--muted)' }}>$</span>
                      <input type="number" value={splitAmounts[jobId]||0} step="0.01" min="0"
                        onChange={e=>setSplitAmounts(prev=>({...prev,[jobId]:parseFloat(e.target.value)||0}))}
                        style={{ ...inlineInput, width:'100px', textAlign:'right', fontFamily:'JetBrains Mono,monospace' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
              <button onClick={()=>setShowSplit(false)} style={{ background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'7px 14px', color:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>Back</button>
              <button onClick={handleConfirmBulk} disabled={bulkSaving} style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'7px 16px', color:'#fff', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', opacity:bulkSaving?0.6:1 }}>
                <i className="ti ti-check" aria-hidden="true"/> {bulkSaving?'Saving...':'Confirm & Assign'}
              </button>
            </div>
          </div>
        )}

        {/* Additional PO bar */}
        {addlMode && !showAddlSplit && (
          <div style={{ background:'var(--blue-light)', border:'1px solid var(--blue)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <span style={{ fontSize:'13px', color:'var(--blue)', fontWeight:'500' }}>{addlSelected.size} job{addlSelected.size!==1?'s':''} selected</span>
            <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
              <input value={addlPO} onChange={e=>setAddlPO(e.target.value.toUpperCase())}
                placeholder="New PO number..." style={{ ...inlineInput, width:'140px', fontSize:'13px', padding:'7px 10px' }}/>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:'13px' }}>$</span>
                <input type="number" value={addlTotal} onChange={e=>handleAddlTotalChange(e.target.value)}
                  placeholder="Total amount" style={{ ...inlineInput, width:'150px', fontSize:'13px', padding:'7px 10px 7px 24px' }}/>
              </div>
              <button onClick={handleShowAddlSplit} style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'7px 14px', color:'#fff', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                <i className="ti ti-calculator" aria-hidden="true"/> Review Split
              </button>
              <button onClick={cancelAddl} style={{ background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'7px 12px', color:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Additional PO split panel */}
        {showAddlSplit && (
          <div style={{ background:'var(--surface)', border:'1px solid var(--blue)', borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px', flexWrap:'wrap', gap:'8px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)' }}>Review price split</span>
                <span style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'12px', fontFamily:'JetBrains Mono,monospace', fontWeight:'600', padding:'2px 8px', borderRadius:'20px' }}>{addlPO}</span>
                <span style={{ fontSize:'12px', color:'var(--muted)' }}>Additional PO</span>
              </div>
              <div style={{ display:'flex', gap:'12px', alignItems:'center', fontSize:'13px', flexWrap:'wrap' }}>
                <span style={{ color:'var(--muted)' }}>Total: <strong style={{ color:'var(--text)', fontFamily:'JetBrains Mono,monospace' }}>${parseFloat(addlTotal).toFixed(2)}</strong></span>
                <span style={{ color:'var(--muted)' }}>Allocated: <strong style={{ color:'var(--green)', fontFamily:'JetBrains Mono,monospace' }}>${addlAllocated.toFixed(2)}</strong></span>
                <span style={{ fontSize:'12px', color: Math.abs(addlRemaining)<0.01 ? 'var(--green)' : addlRemaining>0 ? 'var(--amber)' : 'var(--red)' }}>
                  {Math.abs(addlRemaining)<0.01 ? '✓ Balanced' : addlRemaining>0 ? `$${addlRemaining.toFixed(2)} unallocated` : `$${Math.abs(addlRemaining).toFixed(2)} over`}
                </span>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'14px' }}>
              {[...addlSelected].map(jobId => {
                const job = filteredJobs.find(j=>j._id===jobId);
                if (!job) return null;
                return (
                  <div key={jobId} style={{ display:'flex', alignItems:'center', gap:'10px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 14px', flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'JetBrains Mono,monospace', fontWeight:'500', color:'var(--blue)', minWidth:'80px' }}>{job.stockNum}</span>
                    <span style={{ color:'var(--muted)', fontSize:'13px', flex:1 }}>{job.poNumber||'—'} (existing PO)</span>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <span style={{ fontSize:'13px', color:'var(--muted)' }}>$</span>
                      <input type="number" value={addlSplitAmts[jobId]||0} step="0.01" min="0"
                        onChange={e=>setAddlSplitAmts(prev=>({...prev,[jobId]:parseFloat(e.target.value)||0}))}
                        style={{ ...inlineInput, width:'100px', textAlign:'right', fontFamily:'JetBrains Mono,monospace' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
              <button onClick={()=>setShowAddlSplit(false)} style={{ background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'7px 14px', color:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>Back</button>
              <button onClick={handleConfirmAddl} disabled={addlSaving} style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'7px 16px', color:'#fff', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', opacity:addlSaving?0.6:1 }}>
                <i className="ti ti-check" aria-hidden="true"/> {addlSaving?'Saving...':'Confirm & Assign'}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'10px 14px', marginBottom:'12px', display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
          {addlMode ? (
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <input type="date" value={filters.dateFrom||today} onChange={e=>setFilters(f=>({...f,dateFrom:e.target.value,date:''}))} style={{ ...sel, fontSize:'12px' }}/>
            <span style={{ color:'var(--muted)', fontSize:'12px' }}>to</span>
            <input type="date" value={filters.dateTo||today} onChange={e=>setFilters(f=>({...f,dateTo:e.target.value,date:''}))} style={{ ...sel, fontSize:'12px' }}/>
          </div>
        ) : can('canEditJobs') || can('canDownloadExcel') ? (
          <input type="date" value={filters.date||today} onChange={e=>setFilters(f=>({...f,date:e.target.value,dateFrom:'',dateTo:''}))} style={sel}/>
        ) : (
          <span style={{ ...sel, color:'var(--muted)', background:'var(--bg)' }}>{new Date(new Date().getTime()+8*60*60*1000).toLocaleDateString('en-AU')}</span>
        )}
          <select value={filters.location} onChange={e=>setFilters(f=>({...f,location:e.target.value}))} style={sel}>
            <option value="">All Locations</option>
            {locations.map(l=><option key={l._id} value={l.name}>{l.name}</option>)}
          </select>
          <select value={filters.dealership} onChange={e=>setFilters(f=>({...f,dealership:e.target.value}))} style={sel}>
            <option value="">All Dealerships</option>
            {dealerships.map(d=><option key={d._id} value={d.name}>{d.name}</option>)}
          </select>
          <select value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))} style={sel}>
            <option value="">All Status</option>
            {Object.entries(STATUS_MAP).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={()=>{setFilters({dealership:'',location:'',status:'',date:''}); setSearch('');}} style={{ ...sel, cursor:'pointer', color:'var(--muted)', background:'transparent' }}>Clear</button>
          <div style={{ marginLeft:'auto', display:'flex', gap:'16px', alignItems:'center', fontSize:'13px' }}>
            <span style={{ color:'var(--muted)' }}>{allRows.length} {q?'results':'entries'}</span>
            {can('canViewRevenue') && (
              <span style={{ fontWeight:'600', color:'var(--green)', fontFamily:'JetBrains Mono,monospace' }}>${totalRevenue.toFixed(2)}</span>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'60px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
              <i className="ti ti-loader" style={{ fontSize:'24px', display:'block', marginBottom:'8px', color:'var(--blue)' }} aria-hidden="true"/>
              Loading...
            </div>
          ) : pagedRows.length === 0 ? (
            <div style={{ padding:'60px', textAlign:'center' }}>
              <i className="ti ti-file-description" style={{ fontSize:'40px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <p style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>
                {q ? `No results for "${search}"` : 'No entries for this date'}
              </p>
              <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'16px' }}>
                {q ? 'Try a different search term' : 'Add a new entry to get started'}
              </p>
              {!q && (
                <button onClick={()=>navigate('/jobs/new-entry')}
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--blue)', border:'none', borderRadius:'var(--radius)', padding:'9px 18px', color:'#fff', fontSize:'13px', fontWeight:'500', cursor:'pointer', fontFamily:'inherit' }}>
                  <i className="ti ti-plus" aria-hidden="true"/> New Entry
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="table-scroll">
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'1000px' }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)' }}>
                      {bulkMode && (
                        <th style={{ ...th, width:'40px', textAlign:'center' }}>
                          <input type="checkbox" onChange={e=>{
                            if(e.target.checked) setBulkSelected(new Set(filteredJobs.filter(j=>!j.hasPO).map(j=>j._id)));
                            else setBulkSelected(new Set());
                          }}/>
                        </th>
                      )}
                      {addlMode && (
                        <th style={{ ...th, width:'40px', textAlign:'center' }}>
                          <input type="checkbox" onChange={e=>{
                            if(e.target.checked) setAddlSelected(new Set(filteredJobs.map(j=>j._id)));
                            else setAddlSelected(new Set());
                          }}/>
                        </th>
                      )}
                      {['Date','Location','Dealership','Stock #','VIN','Rego','Services','Extra Notes','Price (excl. GST)','PO Number','Invoice','Status','Actions'].map(h=>(
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map(({ job, ...entry }, rIdx) => {
                      const st = STATUS_MAP[job.status] || STATUS_MAP.submitted;
                      return (
                        <tr key={`${job._id}-${entry.eIdx}`}
                          style={{ borderTop:entry.eIdx>0?'1px dashed var(--border)':'none', transition:'background .1s', background:bulkSelected.has(job._id)?'var(--blue-light)':addlSelected.has(job._id)?'var(--blue-light)':'transparent' }}
                          onMouseEnter={e=>{ if(!bulkSelected.has(job._id)&&!addlSelected.has(job._id)) e.currentTarget.style.background='var(--bg)'; }}
                          onMouseLeave={e=>{ if(!bulkSelected.has(job._id)&&!addlSelected.has(job._id)) e.currentTarget.style.background='transparent'; }}>
                          {bulkMode && entry.eIdx===0 && (
                            <td style={{ ...td, textAlign:'center', width:'40px' }}>
                              <input type="checkbox"
                                checked={bulkSelected.has(job._id)}
                                disabled={!!job.poNumber}
                                onChange={()=>toggleBulkRow(job._id)}/>
                            </td>
                          )}
                          {bulkMode && entry.eIdx>0 && <td style={td}/>}
                          {addlMode && entry.eIdx===0 && (
                            <td style={{ ...td, textAlign:'center', width:'40px' }}>
                              <input type="checkbox"
                                checked={addlSelected.has(job._id)}
                                onChange={()=>toggleAddlRow(job._id)}/>
                            </td>
                          )}
                          {addlMode && entry.eIdx>0 && <td style={td}/>}
                          <td style={td}><span style={{ fontSize:'12px', color:'var(--muted)', whiteSpace:'nowrap' }}>{new Date(job.createdAt).toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric',timeZone:'Australia/Perth'})}</span></td>
                          <td style={td}><span style={{ fontSize:'12px', color:'var(--blue)' }}>{job.location}</span></td>
                          <td style={td}><span style={{ fontSize:'12px', fontWeight:'500', color:'var(--text)' }}>{job.dealership}</span></td>
                          <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--blue)' }}>{job.stockNum||'—'}</span></td>
                          <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'11px', color:'var(--muted)' }}>{job.vinNum||'—'}</span></td>
                          <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', color:'var(--text)' }}>{job.rego||'—'}</span></td>
                          <td style={{ ...td, minWidth:'160px', maxWidth:'240px', whiteSpace:'normal', lineHeight:'1.5' }}>
                            <span style={{ fontSize:'12px', color:'var(--text)' }}>{(entry.services||[]).join(', ')||'—'}</span>
                          </td>
                          <td style={td}><span style={{ fontSize:'12px', color:'var(--muted)' }}>{getRowExtraNotes(job, entry.eIdx)||'—'}</span></td>
                          <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--green)' }}>${(entry.price||0).toFixed(2)}</span></td>

                          {/* PO inline edit */}
                          <td style={td}>
                            {editingPO===`${job._id}-${entry.eIdx}` ? (
                              <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                                <input value={poInput} onChange={e=>setPoInput(e.target.value.toUpperCase())}
                                  style={{ ...inlineInput, width:'90px' }} placeholder="PO #" autoFocus
                                  onKeyDown={e=>{if(e.key==='Enter')savePO(job._id,entry.eIdx);if(e.key==='Escape')setEditingPO(null);}}/>
                                <button onClick={()=>savePO(job._id,entry.eIdx)} style={saveBtn}>✓</button>
                                <button onClick={()=>setEditingPO(null)} style={cancelBtn}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                {entry.poNumber
                                  ? <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--green)' }}>{entry.poNumber}</span>
                                  : <span style={{ background:'var(--amber-bg)', color:'var(--amber)', fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px' }}>Pending</span>}
                                {can('canEditJobs') && (
                            <button onClick={()=>{setEditingPO(`${job._id}-${entry.eIdx}`);setPoInput(entry.poNumber||'');}} style={iconBtn}>
                              <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-label="Edit PO"/>
                            </button>
                          )}
                              </div>
                            )}
                          </td>

                          {/* Invoice inline edit */}
                          <td style={td}>
                            {can('canEditJobs') ? (
                              editingInvoice===`${job._id}-${entry.eIdx}` ? (
                                <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                                  <input value={invoiceInput} onChange={e=>setInvoiceInput(e.target.value.toUpperCase())}
                                    style={{ ...inlineInput, width:'90px' }} placeholder="INV #" autoFocus
                                    onKeyDown={e=>{if(e.key==='Enter')saveInvoice(job._id,entry.eIdx);if(e.key==='Escape')setEditingInvoice(null);}}/>
                                  <button onClick={()=>saveInvoice(job._id, entry.eIdx)} style={{ ...saveBtn, background:'var(--purple)' }}>✓</button>
                                  <button onClick={()=>setEditingInvoice(null)} style={cancelBtn}>✕</button>
                                </div>
                              ) : (
                                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                  {entry.invoiceNumber
                                    ? <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--purple)' }}>{entry.invoiceNumber}</span>
                                    : <span style={{ fontSize:'12px', color:'var(--hint)' }}>—</span>}
                                  <button onClick={()=>{setEditingInvoice(`${job._id}-${entry.eIdx}`);setInvoiceInput(entry.invoiceNumber||'');}} style={iconBtn}>
                                    <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-label="Edit invoice"/>
                                  </button>
                                </div>
                              )
                            ) : (
                              entry.invoiceNumber
                                ? <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--purple)' }}>{entry.invoiceNumber}</span>
                                : <span style={{ fontSize:'12px', color:'var(--hint)' }}>—</span>
                            )}
                          </td>

                          <td style={td}>
                            <span style={{ background:st.bg, color:st.color, fontSize:'11px', fontWeight:'500', padding:'2px 9px', borderRadius:'20px', whiteSpace:'nowrap' }}>{st.label}</span>
                          </td>
                          <td style={td}>
                            <div style={{ display:'flex', gap:'4px' }}>
                              {can('canEditJobs') ? (
                                <>
                                  <button onClick={()=>navigate(`/jobs/${job._id}/edit`)} style={iconBtn}>
                                    <i className="ti ti-edit" style={{ fontSize:'14px' }} aria-label="Edit"/>
                                  </button>
                                  <button onClick={()=>handleDelete(job._id,job.stockNum||job.rego||job.vinNum)} disabled={deletingId===job._id} style={{ ...iconBtn, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                                    <i className="ti ti-trash" style={{ fontSize:'14px' }} aria-label="Delete"/>
                                  </button>
                                </>
                              ) : (
                                <button onClick={()=>navigate(`/jobs/${job._id}/view`)} style={{ ...iconBtn, fontSize:'12px', padding:'4px 10px' }}>View</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination + bottom actions */}
              <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', flexWrap:'wrap' }}>
                {/* Pagination */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                  <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                    style={{ ...iconBtn, opacity:page===1?0.4:1 }}>
                    <i className="ti ti-chevron-left" style={{ fontSize:'14px' }} aria-label="Previous"/>
                  </button>
                  {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
                    <button key={p} onClick={()=>setPage(p)}
                      style={{ ...iconBtn, minWidth:'30px', justifyContent:'center', background:p===page?'var(--blue)':'transparent', color:p===page?'#fff':'var(--muted)', borderColor:p===page?'var(--blue)':'var(--border-md)', fontSize:'12px' }}>
                      {p}
                    </button>
                  ))}
                  <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                    style={{ ...iconBtn, opacity:page===totalPages?0.4:1 }}>
                    <i className="ti ti-chevron-right" style={{ fontSize:'14px' }} aria-label="Next"/>
                  </button>
                  <span style={{ fontSize:'12px', color:'var(--muted)', marginLeft:'4px' }}>
                    {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,allRows.length)} of {allRows.length}
                  </span>
                </div>
                {/* Download at bottom too */}
                <ExcelButtons/>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const th = { padding:'10px 14px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', background:'var(--bg)', whiteSpace:'nowrap' };
const td = { padding:'10px 14px', borderBottom:'1px solid var(--border)', verticalAlign:'middle' };
const sel = { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'7px 10px', fontSize:'13px', color:'var(--text)', outline:'none', fontFamily:'Inter,sans-serif' };
const iconBtn = { background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'4px 8px', cursor:'pointer', color:'var(--muted)', display:'inline-flex', alignItems:'center', justifyContent:'center' };
const inlineInput = { background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'4px 8px', fontSize:'12px', fontFamily:'JetBrains Mono,monospace', outline:'none', color:'var(--text)' };
const saveBtn   = { background:'var(--green)',   border:'none', borderRadius:'var(--radius)', padding:'4px 8px', color:'#fff', fontSize:'12px', cursor:'pointer' };
const cancelBtn = { background:'var(--bg)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'4px 8px', color:'var(--muted)', fontSize:'12px', cursor:'pointer' };
const secBtn    = { display:'inline-flex', alignItems:'center', gap:'5px', background:'transparent', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'6px 12px', color:'var(--muted)', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' };
const menuItem  = { display:'block', width:'100%', padding:'10px 14px', fontSize:'13px', color:'var(--text)', cursor:'pointer', background:'transparent', border:'none', textAlign:'left', fontFamily:'Inter,sans-serif' };
